import axiosInstance from 'config/AxiosConfig';
import { BaseResponse } from 'domain/payload/response/BaseResponseDto';
import { PpobGroupItem, PpobItem, PpobTransactionHistoryCmsResp, PpobTransactionCheckResponseData } from 'domain/payload/response/PPOB/PpobResp';
import {
  ProductItem,
  ProductItemConfigResponse
} from 'domain/payload/response/PPOB/PpobProductItemResp';
import {
  PpobGroupToggleActiveReq,
  PpobGroupUpdateSortReq,
  PpobItemToggleActiveReq,
  PpobItemUpdateSortReq
} from 'domain/payload/request/ppob/PpobReq';
import {
  ProductItemFilterParams,
  UpdatePricingPayload,
  ToggleActivePayload,
  ProductItemExportParams
} from 'domain/payload/request/ppob/PpobProductItemReq';

const PpobService = () => {
  const getGroupList = async (
    token: string,
    search?: string | null,
    page?: number | null,
    size?: number | null
  ): Promise<BaseResponse<PpobGroupItem[]>> => {
    try {
      const response = await axiosInstance.get(`/v1/legacy/cms/ppob/group/list`, {
        headers: {
          Authorization: `Bearer ${token}`
        },
        params: {
          search: search,
          page: page,
          size: size
        }
      });
      return response.data;
    } catch (error: any) {
      throw error.response?.data?.message || 'Failed to fetch PPOB group list';
    }
  };

  const getGroupDetail = async (
    token: string,
    id: string
  ): Promise<BaseResponse<PpobGroupItem>> => {
    try {
      const response = await axiosInstance.get(`/v1/legacy/cms/ppob/group/detail`, {
        headers: {
          Authorization: `Bearer ${token}`
        },
        params: { id }
      });
      return response.data;
    } catch (error: any) {
      throw error.response?.data?.message || 'Failed to fetch PPOB group detail';
    }
  };

  const updateGroupSort = async (
    token: string,
    request: PpobGroupUpdateSortReq
  ): Promise<BaseResponse<null>> => {
    try {
      const response = await axiosInstance.put(`/v1/legacy/cms/ppob/group/update-sort`, request, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      return response.data;
    } catch (error: any) {
      throw error.response?.data?.message || 'Failed to update group sort order';
    }
  };

  const toggleGroupActive = async (
    token: string,
    request: PpobGroupToggleActiveReq
  ): Promise<BaseResponse<null>> => {
    try {
      const response = await axiosInstance.post(`/v1/legacy/cms/ppob/group/toggle-active`, request, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      return response.data;
    } catch (error: any) {
      throw error.response?.data?.message || 'Failed to toggle group active status';
    }
  };

  const getItemList = async (
    token: string,
    groupId: string,
    search?: string | null,
    page?: number | null,
    size?: number | null
  ): Promise<BaseResponse<PpobItem[]>> => {
    try {
      const response = await axiosInstance.get(`/v1/legacy/cms/ppob/product/list`, {
        headers: {
          Authorization: `Bearer ${token}`
        },
        params: {
          groupId: groupId,
          search: search,
          page: page,
          size: size
        }
      });
      return response.data;
    } catch (error: any) {
      throw error.response?.data?.message || 'Failed to fetch PPOB product list';
    }
  };

  const getItemDetail = async (
    token: string,
    id: string
  ): Promise<BaseResponse<PpobItem>> => {
    try {
      const response = await axiosInstance.get(`/v1/legacy/cms/ppob/product/detail`, {
        headers: {
          Authorization: `Bearer ${token}`
        },
        params: { id }
      });
      return response.data;
    } catch (error: any) {
      throw error.response?.data?.message || 'Failed to fetch PPOB product detail';
    }
  };

  const updateItemSort = async (
    token: string,
    request: PpobItemUpdateSortReq
  ): Promise<BaseResponse<null>> => {
    try {
      const response = await axiosInstance.put(`/v1/legacy/cms/ppob/product/update-sort`, request, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      return response.data;
    } catch (error: any) {
      throw error.response?.data?.message || 'Failed to update product sort order';
    }
  };

  const toggleItemActive = async (
    token: string,
    request: PpobItemToggleActiveReq
  ): Promise<BaseResponse<null>> => {
    try {
      const response = await axiosInstance.post(`/v1/legacy/cms/ppob/product/toggle-active`, request, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      return response.data;
    } catch (error: any) {
      throw error.response?.data?.message || 'Failed to toggle product active status';
    }
  };

  const getTransactionHistory = async (
    token: string,
    search?: string | null,
    startDate?: string | null,
    endDate?: string | null,
    source?: string | null,
    page?: number | null,
    size?: number | null
  ): Promise<BaseResponse<PpobTransactionHistoryCmsResp[]>> => {
    try {
      const response = await axiosInstance.get(`/v1/legacy/cms/ppob/transaction/history`, {
        headers: {
          Authorization: `Bearer ${token}`
        },
        params: {
          search: search || undefined,
          startDate: startDate || undefined,
          endDate: endDate || undefined,
          source: source || undefined,
          page: page || undefined,
          size: size || undefined,
        }
      });
      return response.data;
    } catch (error: any) {
      throw error.response?.data?.message || 'Failed to fetch PPOB transaction history';
    }
  };

  const getTransactionDetail = async (
    token: string,
    id: string
  ): Promise<BaseResponse<PpobTransactionCheckResponseData>> => {
    try {
      const response = await axiosInstance.get(`/v1/legacy/cms/ppob/transaction/detail/${id}`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      return response.data;
    } catch (error: any) {
      throw error.response?.data?.message || 'Failed to fetch PPOB transaction detail';
    }
  };

  const getProductItemConfig = async (token: string): Promise<BaseResponse<ProductItemConfigResponse>> => {
    try {
      const response = await axiosInstance.get(`/v1/legacy/cms/ppob/product-item/config`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      return response.data;
    } catch (error: any) {
      throw error.response?.data?.message || 'Failed to fetch PPOB product item config';
    }
  };

  const getProductItemList = async (
    token: string,
    params: ProductItemFilterParams
  ): Promise<BaseResponse<ProductItem[]>> => {
    try {
      const response = await axiosInstance.get(`/v1/legacy/cms/ppob/product-item/list`, {
        headers: {
          Authorization: `Bearer ${token}`
        },
        params: {
          page: params.page,
          size: params.size,
          search: params.search || undefined,
          groupId: params.groupId || undefined,
          productId: params.productId || undefined,
        }
      });
      return response.data;
    } catch (error: any) {
      throw error.response?.data?.message || 'Failed to fetch PPOB product item list';
    }
  };

  const getProductItemDetail = async (
    token: string,
    id: string
  ): Promise<BaseResponse<ProductItem>> => {
    try {
      const response = await axiosInstance.get(`/v1/legacy/cms/ppob/product-item/detail`, {
        headers: {
          Authorization: `Bearer ${token}`
        },
        params: { id }
      });
      return response.data;
    } catch (error: any) {
      throw error.response?.data?.message || 'Failed to fetch PPOB product item detail';
    }
  };

  const updateProductItemPricing = async (
    token: string,
    payload: UpdatePricingPayload
  ): Promise<BaseResponse<null>> => {
    try {
      const response = await axiosInstance.put(`/v1/legacy/cms/ppob/product-item/update-pricing`, payload, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      return response.data;
    } catch (error: any) {
      throw error.response?.data?.message || 'Failed to update PPOB product item pricing';
    }
  };

  const toggleProductItemActive = async (
    token: string,
    payload: ToggleActivePayload
  ): Promise<BaseResponse<null>> => {
    try {
      const response = await axiosInstance.post(`/v1/legacy/cms/ppob/product-item/toggle-active`, payload, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      return response.data;
    } catch (error: any) {
      throw error.response?.data?.message || 'Failed to toggle PPOB product item active status';
    }
  };

  const exportProductItems = async (
    token: string,
    params?: ProductItemExportParams
  ): Promise<void> => {
    try {
      const response = await axiosInstance.get(`/v1/legacy/cms/ppob/product-item/export`, {
        headers: {
          Authorization: `Bearer ${token}`
        },
        params: {
          format: params?.format || 'xlsx',
          groupId: params?.groupId || undefined,
          productId: params?.productId || undefined,
          search: params?.search || undefined,
        },
        responseType: 'blob'
      });

      const blob = new Blob([response.data]);
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.setAttribute('download', `ppob_product_items_${Date.now()}.${params?.format || 'xlsx'}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(downloadUrl);
    } catch (error: any) {
      throw error.response?.data?.message || 'Failed to export PPOB product items';
    }
  };

  return {
    getGroupList,
    getGroupDetail,
    updateGroupSort,
    toggleGroupActive,
    getItemList,
    getItemDetail,
    updateItemSort,
    toggleItemActive,
    getProductList: getItemList,
    getProductDetail: getItemDetail,
    updateProductSort: updateItemSort,
    toggleProductActive: toggleItemActive,
    getTransactionHistory,
    getTransactionDetail,
    getProductItemConfig,
    getProductItemList,
    getProductItemDetail,
    updateProductItemPricing,
    toggleProductItemActive,
    exportProductItems,
  };

};

export default PpobService;